'use client';

import { recordAnalyticsDiagnostic } from './analyticsDiagnostics';
import logger from '@/utils/client-logger';

/** 固定已部署地址，不改变 CDN、CSP 域名或 Classic 版本。 */
export const SENSORS_SCRIPT_URL =
  'https://common.xmslol.com/libs/sensorsdata@1.26.18/web/sensorsdata.js';
/** 中性 Classic 文件名沿用既有反误拦截配置。 */
export const CLASSIC_SCRIPT_URL = '/9d5ce3177176.js';

/** 动态节点只能使用调用方明确提供的安全属性。 */
interface AnalyticsScriptOptions {
  id: string;
  src: string;
  nonce?: string;
}

/** 同一 document 的成功和在途 Promise 都永久复用。 */
const documentLoads = new WeakMap<Document, Map<string, Promise<void>>>();
/** 同页额外尝试预算不能通过重建失败 Promise 绕过。 */
const documentAttempts = new WeakMap<Document, Map<string, number>>();

/** body 尾组件拥有网络启动权；模块与 provider 可先创建而不触发外链。 */
const bootstrapDocuments = new WeakMap<
  Document,
  { ready: Promise<void>; activate: () => void; mode?: 'deferred' | 'legacy' }
>();

/** 创建文档级启动门，不依赖组件 mount 次数。 */
function getBootstrapGate() {
  let gate = bootstrapDocuments.get(document);
  if (!gate) {
    let activate!: () => void;
    const ready = new Promise<void>((resolve) => {
      activate = resolve;
    });
    gate = { ready, activate };
    bootstrapDocuments.set(document, gate);
  }
  return gate;
}

/** 文档启动模式首次设置后固定；legacy允许配置入口提前启动但仍串行。 */
export function activateAnalyticsBootstrap(mode: 'deferred' | 'legacy' = 'deferred'): void {
  const gate = getBootstrapGate();
  if (gate.mode && gate.mode !== mode) return;
  gate.mode = mode;
  gate.activate();
}

/** body 尾异步加载；软超时只诊断，只有明确 error 才有界重试。 */
export function loadAnalyticsScript(options: AnalyticsScriptOptions): Promise<void> {
  if (typeof document === 'undefined') return Promise.reject(new Error('analytics_no_document'));
  let loads = documentLoads.get(document);
  if (!loads) {
    loads = new Map();
    documentLoads.set(document, loads);
  }
  const existing = loads.get(options.src);
  if (existing) return existing;

  const provider =
    options.id === 'sensors-sdk'
      ? 'sensors'
      : options.id === 'snowplow-lite'
        ? 'snowplow'
        : 'other';
  const promise = new Promise<void>((resolve, reject) => {
    const started = Date.now();
    let budgets = documentAttempts.get(document);
    if (!budgets) {
      budgets = new Map();
      documentAttempts.set(document, budgets);
    }
    let attempts = budgets.get(options.src) ?? 0;
    /** 每次明确失败后退避重试，禁止为仍在途的脚本创建第二个节点。 */
    const attempt = () => {
      if (attempts >= 3) {
        reject(new Error('analytics_retry_exhausted'));
        return;
      }
      attempts += 1;
      budgets.set(options.src, attempts);
      const previous = document.getElementById(options.id) as HTMLScriptElement | null;
      // 固定 ID 不能作为脚本身份凭据；只接管相同类型、相同绝对 URL 的节点。
      if (
        previous &&
        (previous.tagName !== 'SCRIPT' ||
          previous.src !== new URL(options.src, document.baseURI).href)
      ) {
        reject(new Error('analytics_script_conflict'));
        return;
      }
      const script = previous ?? document.createElement('script');
      const slow = setTimeout(() => {
        logger.warn('[analytics_loader] slow', options.id);
        recordAnalyticsDiagnostic('loader_slow', provider);
      }, 10_000);
      const degraded = setTimeout(() => {
        logger.warn('[analytics_loader] degraded', options.id);
        recordAnalyticsDiagnostic('loader_degraded', provider);
      }, 30_000);
      /** 终态移除本轮监听和诊断定时器。 */
      const cleanup = () => {
        clearTimeout(slow);
        clearTimeout(degraded);
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);
      };
      /** 晚到成功接管原节点，绝不重新请求或初始化。 */
      const onLoad = () => {
        cleanup();
        script.dataset.analyticsLoaded = 'true';
        resolve();
      };
      /** 只有浏览器明确失败才移除节点并考虑下一次尝试。 */
      const onError = () => {
        cleanup();
        script.remove();
        if (attempts < 3 && Date.now() - started < 55_000) {
          setTimeout(attempt, attempts * 500 + Math.floor(Math.random() * 250));
        } else {
          reject(new Error('analytics_script_failed'));
        }
      };
      if (script.dataset.analyticsLoaded === 'true') {
        cleanup();
        resolve();
        return;
      }
      script.addEventListener('load', onLoad);
      script.addEventListener('error', onError);
      if (!previous) {
        script.id = options.id;
        script.async = true;
        script.src = options.src;
        const nonce =
          options.nonce || document.querySelector<HTMLScriptElement>('script[nonce]')?.nonce;
        if (nonce) script.nonce = nonce;
        try {
          document.body.appendChild(script);
        } catch {
          cleanup();
          script.remove();
          reject(new Error('analytics_script_insert_failed'));
        }
      }
    };
    void getBootstrapGate().ready.then(attempt).catch(reject);
  });
  loads.set(options.src, promise);
  // 消费方可观察失败；失败后下次显式启动能够重建 Promise。
  void promise.catch(() => {
    loads?.delete(options.src);
    recordAnalyticsDiagnostic('loader_failed', provider);
  });
  return promise;
}

/** 神策身份 ready 后由 Browser provider 调用；WebView 不应调用此入口。 */
export async function ensureClassicTracker(): Promise<void> {
  const { getSensorsReady } = await import('./providers/sensorProvider');
  await getSensorsReady();
  const browser = window as unknown as {
    GlobalSnowplowNamespace?: string[];
    snowplow?: { (...args: unknown[]): void; q?: unknown[] };
  };
  browser.GlobalSnowplowNamespace ??= [];
  if (!browser.GlobalSnowplowNamespace.includes('snowplow'))
    browser.GlobalSnowplowNamespace.push('snowplow');
  if (!browser.snowplow) {
    /** Classic 加载前只准备命令队列，SDK 初始化仍在调用方完成。 */
    const queue = (...args: unknown[]) => {
      queue.q.push(args);
    };
    queue.q = [] as unknown[];
    browser.snowplow = queue;
  }
  await loadAnalyticsScript({ id: 'snowplow-lite', src: CLASSIC_SCRIPT_URL });
}
